import _ from 'lodash';
import moment from 'moment';
import {
  LIST_RUN_EVENT,
  RR_INTERVAL_MENU,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_SPECIAL_EVENT_ENUM,
} from '@/shared/config';
import type { IEcgBookmark } from '@/shared/model';

// TODO(ts): typed as the group-event-change payload once Apollo domains land
type GroupEventChange = any;

/**
 * Resolves the active toolbar button against the RR-interval menu entries. Finds the menu item whose id matches, so the result is truthy only while an RR-interval mode is selected.
 *
 * @param activeButton - Id of the currently active toolbar button.
 * @returns The matching RR-interval menu entry, or undefined.
 */
export const isRrInterval = (activeButton: string) =>
  RR_INTERVAL_MENU.find((x) => x.id === activeButton);

export const eventTypeConvert = {
  AFIB: 'AFib',
  TACHY: 'Tachy',
  BRADY: 'Brady',
  PAUSE: 'Pause',
  MAX_HR: 'Max HR',
  MIN_HR: 'Min HR',
  SINGLE_VE: 'Single VE',
  SINGLE_SVE: 'Single SVE',
  VE_TRIGEMINAL: 'VE Trigeminy',
  VE_COUPLET: 'VE Couplet',
  VE_BIGEMINY: 'VE Bigeminy',
  VE_QUADRIGEMINY: 'VE Quadrigeminy',
  VE_RUN: 'VE Run',
  SVE_RUN: 'SVE Run',
  SVE_COUPLET: 'SVE Couplet',
  SVE_BIGEMINY: 'SVE Bigeminy',
  SVE_TRIGEMINAL: 'SVE Trigeminy',
  SVE_QUADRIGEMINY: 'SVE Quadrigeminy',
  SVT: 'SVT',
  VT: 'VT',
  AVB3: '3rd Deg HB',
  AVB2: '2nd Deg HB',
  // AVB1: '1st Deg HB',
  ARTIFACT: 'Artifact',
  Deleted: 'Deleted',
  BASELINE: 'Baseline',
};

export const convertEventNameFromType = {
  AFIB: 'AFib',
  TACHY: 'Tachy',
  BRADY: 'Brady',
  PAUSE: 'Pause',
  MAX_HR: 'Max Heart Rate',
  MIN_HR: 'Min Heart Rate',
  SINGLE_VE: 'Single VE',
  SINGLE_SVE: 'Single SVE',
  VE_TRIGEMINAL: 'VE Trigeminy',
  VE_COUPLET: 'VE Couplet',
  VE_BIGEMINY: 'VE Bigeminy',
  VE_QUADRIGEMINY: 'VE Quadrigeminy',
  VE_RUN: 'VE Run',
  SVE_RUN: 'SVE Run',
  SVE_COUPLET: 'SVE Couplet',
  SVE_BIGEMINY: 'SVE Bigeminy',
  SVE_TRIGEMINAL: 'SVE Trigeminy',
  SVE_QUADRIGEMINY: 'SVE Quadrigeminy',
  SVT: 'SVT',
  VT: 'VT',
  AVB3: '3rd Deg HB',
  AVB2: '2nd Deg HB',
  // AVB1: '1st Deg HB',
  ARTIFACT: 'Artifact',
  Deleted: 'Deleted',
  BASELINE: 'Baseline',
};

// TODO(ts): typed as the holter task queue payload once Apollo domains land
/**
 * Decides whether the current study may be queued for holter processing. Gathers the friendly ids of pending profiles and running tasks, lets the study through when it already appears there, and otherwise only while the queue is shorter than limitWorker.
 *
 * @param holterTasks - Worker queue carrying pendingProfiles, tasks and limitWorker.
 * @param currentStudyId - Friendly id of the study being checked.
 * @returns True when the study is already processing or a worker slot is free.
 */
export const isHolterProcessingAvailable = (holterTasks: any, currentStudyId: string) => {
  const { pendingProfiles, tasks, limitWorker } = holterTasks;
  const pendingStudyList = _.map(pendingProfiles, (x) => x?.study?.friendlyId);
  const tasksStudyList = _.map(tasks, (x) => x?.study?.friendlyId);
  const listStudyProcess = [...pendingStudyList, ...tasksStudyList];
  const isProcessing = listStudyProcess.includes(currentStudyId);
  if (isProcessing) return true;
  if (listStudyProcess.length < limitWorker) {
    return true;
  }
  return false;
};

// TODO(ts): typed as the processing-limit payload once Apollo domains land
/**
 * Reports whether the AI worker pool has no free slot left for another study. Returns false as soon as the pool is ready, and true only when both processingStudyFids and simultaneousStudyLimit are present and the number of in-flight studies has reached that limit.
 *
 * @param info - Worker pool snapshot carrying isReady, processingStudyFids and simultaneousStudyLimit.
 * @returns True when the simultaneous-study limit is reached and no further study may start.
 */
export const checkLimitStudyProcessing = (info: any) => {
  const { isReady, processingStudyFids, simultaneousStudyLimit } = info || {};
  if (isReady) return false;
  if (
    !_.isNil(processingStudyFids) &&
    !_.isNil(simultaneousStudyLimit) &&
    processingStudyFids.length >= simultaneousStudyLimit
  )
    return true;
  return false;
};

/**
 * Reinterprets an ISO timestamp in a study's recording timezone. Parses the input with moment and shifts its utcOffset by the given number of minutes without keeping local time, falling back to a moment built from null when no date is supplied.
 *
 * @param isoDate - Timestamp to reinterpret.
 * @param timezoneOffset - Offset in minutes to apply; defaults to 0.
 * @returns A moment positioned at the requested UTC offset, or an invalid moment when no date was given.
 */
export const convertToMomentJs = (isoDate: moment.MomentInput, timezoneOffset = 0) => {
  if (isoDate) {
    const date = moment(isoDate);
    date.utcOffset(timezoneOffset, false);
    return date;
  }
  return moment(null);
};

/**
 * Renders an event duration as an hours, minutes and seconds label. Rounds the absolute seconds into a moment duration, folds whole days back into the hour count, and strips any leading zero-valued unit out of the assembled text.
 *
 * @param diffInSecond - Duration in seconds.
 * @returns A trimmed duration label such as 2h 5m 3s.
 */
export const formatDurationAsHms = (diffInSecond: number) => {
  const duration = moment.duration(Math.abs(Math.round(diffInSecond)), 'second');
  const hours = duration.days() * 24 + duration.hours();
  const formatDuration = `${hours}h ${duration.minutes()}m ${duration.seconds()}s`;
  return formatDuration.replace(/\b0+[a-z]+\s*/gi, '').trim();
};

/**
 * Formats the elapsed time of an ECG event from its start and stop timestamps. Computes the difference in seconds, delegating to the hours-minutes-seconds formatter at a minute or more and otherwise rendering seconds with at most one decimal.
 *
 * @param start - Event start timestamp.
 * @param stop - Event stop timestamp.
 * @returns A duration label for the event span.
 */
export const getEventDuration = (start: string | number | Date, stop: string | number | Date) => {
  const diffInSecond = (new Date(stop).getTime() - new Date(start).getTime()) / 1000;
  if (diffInSecond >= 60) {
    return formatDurationAsHms(diffInSecond);
  }

  const durationAsDecimalSecond = Math.abs(diffInSecond);
  const formatDurationAsDecimalSecond =
    durationAsDecimalSecond % 1 === 0
      ? durationAsDecimalSecond
      : durationAsDecimalSecond.toFixed(1);
  const parseFloatValue = parseFloat(String(formatDurationAsDecimalSecond));
  if (parseFloatValue >= 60) {
    return formatDurationAsHms(parseFloatValue);
  }

  const formatDuration = `${
    Number(formatDurationAsDecimalSecond) % 1 === 0
      ? Math.trunc(Number(formatDurationAsDecimalSecond))
      : formatDurationAsDecimalSecond
  }s`;
  return formatDuration.replace(/\b0+[a-z]+\s*/gi, '').trim();
};

/**
 * Measures an ECG event span in seconds. Subtracts the two timestamps in milliseconds, divides by 1000 and takes the absolute value so argument order does not matter.
 *
 * @param start - Event start timestamp.
 * @param stop - Event stop timestamp.
 * @returns The event length in seconds.
 */
export const getEventDurationInSecond = (
  start: string | number | Date,
  stop: string | number | Date,
) => Math.abs((new Date(stop).getTime() - new Date(start).getTime()) / 1000);

/**
 * Formats an event duration that is already expressed in seconds. Delegates to the hours-minutes-seconds formatter at or above 60 seconds, otherwise renders the absolute value with one decimal unless it is whole, and re-checks the 60-second threshold after that rounding.
 *
 * @param diffInSecond - Duration in seconds.
 * @returns A seconds label, or an hours-minutes-seconds label for a minute or more.
 */
export const getDurationBySecond = (diffInSecond: number) => {
  if (diffInSecond >= 60) {
    return formatDurationAsHms(diffInSecond);
  }

  const durationAsDecimalSecond = Math.abs(diffInSecond);
  const formatDurationAsDecimalSecond =
    durationAsDecimalSecond % 1 === 0
      ? durationAsDecimalSecond
      : durationAsDecimalSecond.toFixed(1);
  const parseFloatValue = parseFloat(String(formatDurationAsDecimalSecond));
  if (parseFloatValue >= 60) {
    return formatDurationAsHms(parseFloatValue);
  }

  const formatDuration = `${
    Number(formatDurationAsDecimalSecond) % 1 === 0
      ? Math.trunc(Number(formatDurationAsDecimalSecond))
      : formatDurationAsDecimalSecond
  }s`;
  return formatDuration.replace(/\b0+[a-z]+\s*/gi, '').trim();
};

/**
 * Enumerates every calendar day a study spans in its recording timezone. Snaps start to the beginning of its day and stop to the end of its day at the given offset, pushes one ISO string per day across the difference, and de-duplicates the list.
 *
 * @param start - First timestamp of the span.
 * @param stop - Last timestamp of the span.
 * @param timezoneOffset - Offset in minutes used to define day boundaries; defaults to 0.
 * @returns An array of ISO day-start timestamps, one per day covered.
 */
export const generateListDate = (
  start: moment.MomentInput,
  stop: moment.MomentInput,
  timezoneOffset = 0,
) => {
  const listDate = [];
  const startDate = convertToMomentJs(start, timezoneOffset).startOf('days');
  const stopDate = convertToMomentJs(stop, timezoneOffset).endOf('days');
  const fromToLength = stopDate.diff(startDate, 'days');
  for (let i = 0; i <= fromToLength; i += 1) {
    listDate.push(startDate.clone().add(i, 'days').toISOString());
  }
  return _.uniq(listDate);
};

/**
 * Spells out a tracked time span in hours, minutes and seconds words. Splits the seconds into the three units and prefixes each larger unit only when it is non-zero, returning an ellipsis for a falsy input.
 *
 * @param timeInSeconds - Tracked time in seconds.
 * @returns A label such as 1 hrs 5 min 3 sec, or an ellipsis when there is no time yet.
 */
export const getTimeString = (timeInSeconds: number) => {
  if (timeInSeconds) {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor(timeInSeconds / 60 - hours * 60);
    const seconds = Math.floor(timeInSeconds - minutes * 60 - hours * 3600);
    let timeString = `${seconds} sec`;
    if (minutes) {
      timeString = `${minutes} min ${timeString}`;
    }
    if (hours) {
      timeString = `${hours} hrs ${timeString}`;
    }
    return timeString;
  }
  return '...';
};

/**
 * Reshapes ECG bookmarks into the strip-like records the report timeline draws. For each bookmark it computes the midpoint epoch between start and stop, floors that midpoint to the minute for startTimeEpoch, carries id, start, stop and notes across, and flags the record with isBookmarkStrip.
 *
 * @param ecgBookmarks - Bookmarks to reshape; defaults to an empty list.
 * @returns An array of bookmark strip records carrying center and startTimeEpoch.
 */
export const formatEcgBookmarks = (ecgBookmarks: IEcgBookmark[] = []) =>
  _.map(ecgBookmarks, (bookmark) => {
    const startDate = moment(bookmark.start);
    const diffEpoch = moment(bookmark.stop).diff(startDate);
    const centerEpoch = startDate.valueOf() + Math.round(diffEpoch / 2);
    return {
      id: bookmark.id,
      start: bookmark.start,
      stop: bookmark.stop,
      center: centerEpoch,
      notes: bookmark.notes,
      startTimeEpoch: moment(centerEpoch).startOf('minutes').valueOf(),
      isBookmarkStrip: true,
    };
  });

export const checkForHexRegExp = /^[0-9a-fA-F]{24}$/;

// TODO(ts): typed as the report/study payload once Apollo domains land
/**
 * Derives the AI status label shown for a study's report. Yields N/A for an empty study, Ready when an UNREVIEWED or Interim report has a Done or Completed holter profile status or when processingAiStatus is Done and AI is not disabled, Running while processing or rendering is still under way, and Not ready otherwise.
 *
 * @param reportData - Report payload carrying study, report and holterProfile.
 * @returns One of the labels N/A, Ready, Running or Not ready.
 */
export const generateAiStatusReport = (reportData: any) => {
  // AI Status
  const { study, report, holterProfile } = reportData || {};
  const { processingAiStatus, aiReport } = study;
  const { type } = report || {};
  if (_.isEmpty(study)) {
    return 'N/A';
  }
  if (
    ['UNREVIEWED', 'Interim'].includes(type) &&
    ['Done', 'Completed'].includes(holterProfile?.processingAiStatus?.status)
  ) {
    return 'Ready';
  }
  if (
    processingAiStatus === 'Done' ||
    (aiReport && ['Generated', 'Rendering'].includes(aiReport.status))
  ) {
    if (study.isDisableAi) {
      return 'Running';
    }
    return 'Ready';
  }
  if (
    !['Done', 'None'].includes(processingAiStatus) || // None, Pending, Started, Processing, Completed, Done
    (aiReport && !['Generated', 'Rendering'].includes(aiReport.status))
  ) {
    return 'Running';
  }
  return 'Not ready';
};

/**
 * Maps a bulk group-event action onto the beat or rhythm event type it produces. Switches delete onto the deleted beat type, markedAsArtifact onto artifact, and the changeAllTo actions onto VT, SVT, VE run and SVE run respectively, yielding undefined for anything unrecognised.
 *
 * @param actionType - Bulk action selected on a group of events.
 * @returns The target event type enum value, or undefined for an unmapped action.
 */
export const getEventTypeByGroupAction = (actionType: string) => {
  switch (actionType) {
    case 'delete':
      return TYPE_BEAT_ENUM.DELETED;
    case 'markedAsArtifact':
      return TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
    case 'changeAllToVT':
      return TYPE_RHYTHM_EVENT_ENUM.VT;
    case 'changeAllToSVT':
      return TYPE_RHYTHM_EVENT_ENUM.SVT;
    case 'changeAllToVE':
      return TYPE_BEAT_EVENT_ENUM.VE_RUN;
    case 'changeAllToSVE':
      return TYPE_BEAT_EVENT_ENUM.SVE_RUN;
    default:
      return undefined;
  }
};

const SPECIAL_EVENT_FILTER_TYPE_MAP = {
  [TYPE_RHYTHM_EVENT_ENUM.TACHY]: {
    [TYPE_SPECIAL_EVENT_ENUM.SINUS_TACHY]: 'sinusTachy',
    [TYPE_SPECIAL_EVENT_ENUM.HIGH_TACHY]: 'highTachy',
  },
  [TYPE_RHYTHM_EVENT_ENUM.BRADY]: {
    [TYPE_SPECIAL_EVENT_ENUM.SINUS_BRADY]: 'sinusBrady',
    [TYPE_SPECIAL_EVENT_ENUM.LOW_BRADY]: 'lowBrady',
  },
  [TYPE_RHYTHM_EVENT_ENUM.PAUSE]: {
    [TYPE_SPECIAL_EVENT_ENUM.SHORT_PAUSE]: 'shortPause',
    [TYPE_SPECIAL_EVENT_ENUM.LONG_PAUSE]: 'longPause',
  },
};

/**
 * Looks up the filter token describing a special event selection inside one group event type. Indexes the special-event filter map first by group type and then by the selected special type, yielding undefined when either level is absent.
 *
 * @param groupEventType - Group event type whose special filters are being resolved.
 * @param selectedSpecialType - Special event type currently selected for that group.
 * @returns The mapped special-event filter token, or undefined when unmapped.
 */
const getSpecialEventFilterType = (groupEventType: string, selectedSpecialType: string) =>
  (SPECIAL_EVENT_FILTER_TYPE_MAP as Record<string, Record<string, string>>)[groupEventType]?.[
    selectedSpecialType
  ];

/**
 * Builds the mutation payload that applies bulk group event changes on the server. Sorts each change into the deleted, markedAsArtifact or runEvents bucket with its filter tokens, maps run actions to their target type while keeping the original as oldType, nulls out empty buckets, and returns an empty object when nothing changed.
 *
 * @param groupEventChanges - Pending group changes to send; defaults to an empty list.
 * @returns A payload carrying deleted, markedAsArtifact and runEvents, or an empty object when there is nothing to send.
 */
export const buildGroupEventsPayload = (groupEventChanges: GroupEventChange[] = []) => {
  if (_.isEmpty(groupEventChanges)) {
    return {};
  }
  const payload: {
    deleted: any[] | null;
    markedAsArtifact: any[] | null;
    runEvents: any[] | null;
  } = {
    deleted: [],
    markedAsArtifact: [],
    runEvents: [],
  };
  _.forEach(groupEventChanges, (item) => {
    if (!item?.groupEventType || !item?.actionType) {
      return;
    }
    const listFilterTypes = buildGroupEventFilterTypes([item]);

    if (item.actionType === 'delete') {
      payload.deleted.push({
        type: item.groupEventType,
        listFilterTypes,
      });
      return;
    }
    if (item.actionType === 'markedAsArtifact') {
      payload.markedAsArtifact.push({
        type: item.groupEventType,
        listFilterTypes,
      });
      return;
    }
    const mappedType = getEventTypeByGroupAction(item.actionType);
    if (mappedType) {
      payload.runEvents.push({
        type: mappedType,
        listFilterTypes,
        oldType: item.groupEventType,
      });
    }
  });

  payload.deleted = payload.deleted.length ? payload.deleted : null;
  payload.markedAsArtifact = payload.markedAsArtifact.length ? payload.markedAsArtifact : null;
  payload.runEvents = payload.runEvents.length ? payload.runEvents : null;

  const hasChanges = payload.deleted || payload.markedAsArtifact || payload.runEvents;
  return hasChanges ? payload : {};
};

/**
 * Collects the filter tokens that narrow which events a group change applies to. Walks each change's filter snapshot, adding isCaptured when capture filtering was on, isAvgHrBelow100 for run event types, and the mapped special-event token unless the selection was all HR ranges, then de-duplicates the result.
 *
 * @param groupEventChanges - Pending group changes with their filter snapshots; defaults to an empty list.
 * @returns A de-duplicated array of filter-type tokens.
 */
export function buildGroupEventFilterTypes(groupEventChanges: GroupEventChange[] = []) {
  if (_.isEmpty(groupEventChanges)) {
    return [];
  }

  const listFilterTypes: string[] = [];
  _.forEach(groupEventChanges, (item) => {
    const eventFilter = item?.filterSnapshot?.eventFilter || {};
    const selectedSpecialType = item?.filterSnapshot?.specialEventTypes?.[item?.groupEventType];

    if (eventFilter.isCapture) {
      listFilterTypes.push('isCaptured');
    }
    if (eventFilter.isAvgHrBelow100 && LIST_RUN_EVENT.includes(item?.groupEventType)) {
      listFilterTypes.push('isAvgHrBelow100');
    }
    if (selectedSpecialType && selectedSpecialType !== TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES) {
      const mappedSpecialFilterType = getSpecialEventFilterType(
        item.groupEventType,
        selectedSpecialType,
      );
      if (mappedSpecialFilterType) {
        listFilterTypes.push(mappedSpecialFilterType);
      }
    }
  });
  return _.uniq(listFilterTypes);
}

/**
 * Builds the payload marking whole event groups as reviewed or unreviewed. Keeps only the markedAsReviewed and markedAsUnreviewed changes, attaches each one's filter tokens, and splits them into the two lists.
 *
 * @param groupEventChanges - Pending group changes to inspect; defaults to an empty list.
 * @returns An object carrying the reviewedEvents and unReviewedEvents arrays.
 */
export const buildReviewHolterEventsPayload = (groupEventChanges: GroupEventChange[] = []) => {
  if (_.isEmpty(groupEventChanges)) {
    return {
      reviewedEvents: [],
      unReviewedEvents: [],
    };
  }

  const reviewedEvents: any[] = [];
  const unReviewedEvents: any[] = [];

  _.forEach(groupEventChanges, (item) => {
    if (!item?.groupEventType || !item?.actionType) {
      return;
    }
    if (!['markedAsReviewed', 'markedAsUnreviewed'].includes(item.actionType)) {
      return;
    }

    const listFilterTypes = buildGroupEventFilterTypes([item]);
    const reviewEvent = {
      type: item.groupEventType,
      listFilterTypes,
    };

    if (item.actionType === 'markedAsReviewed') {
      reviewedEvents.push(reviewEvent);
      return;
    }
    unReviewedEvents.push(reviewEvent);
  });

  return {
    reviewedEvents,
    unReviewedEvents,
  };
};

/**
 * Expresses a raw byte count in gigabytes for storage readouts. Divides by 1024 cubed and fixes the result to two decimal places.
 *
 * @param bytes - Size in bytes.
 * @returns The gigabyte value as a string with two decimals.
 */
export const convertBytesToGB = (bytes: number) => (bytes / 1024 ** 3).toFixed(2);
